import React, { useRef, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    Animated,
    PanResponder,
    TouchableOpacity,
    useWindowDimensions,
    KeyboardAvoidingView,
    Platform,
} from 'react-native';
import { WorkpadConfig } from '@eduwrite/shared';
import { useWorkpadStore } from '../store/useWorkpadStore';
import { FreeDrawCanvas } from './FreeDrawCanvas';
import { GuidedStepRenderer } from './GuidedStepRenderer';

interface WorkpadSheetProps {
    config: WorkpadConfig;
}

export const WorkpadSheet: React.FC<WorkpadSheetProps> = ({ config }) => {
    const { height } = useWindowDimensions();
    const sheetHeight = height * 0.6;
    const translateY = useRef(new Animated.Value(sheetHeight)).current;
    const { isOpen, mode, close, clearStrokes, activeConfig } = useWorkpadStore();

    // WP-028: prefer the dynamically-set activeConfig (e.g. from a choice.guidedAssist
    // scaffold) over the activity-level config prop so GuidedStepRenderer gets the right steps.
    const effectiveConfig = activeConfig || config;

    // Animate in when open changes to true
    React.useEffect(() => {
        if (isOpen) {
            Animated.spring(translateY, {
                toValue: 0,
                useNativeDriver: true,
                friction: 8,
                tension: 40,
            }).start();
        } else {
            Animated.timing(translateY, {
                toValue: sheetHeight,
                duration: 200,
                useNativeDriver: true,
            }).start();
        }
    }, [isOpen, sheetHeight, translateY]);

    const panResponder = useRef(
        PanResponder.create({
            onStartShouldSetPanResponder: () => false,
            onMoveShouldSetPanResponder: (_evt, gestureState) => gestureState.dy > 5,
            onPanResponderMove: (_evt, gestureState) => {
                if (gestureState.dy > 0) {
                    translateY.setValue(gestureState.dy);
                }
            },
            onPanResponderRelease: (_evt, gestureState) => {
                if (gestureState.dy > sheetHeight * 0.25) {
                    close();
                } else {
                    Animated.spring(translateY, {
                        toValue: 0,
                        useNativeDriver: true,
                        friction: 8,
                    }).start();
                }
            },
        })
    ).current;

    const handleDone = useCallback(() => {
        close();
    }, [close]);

    if (!isOpen) return null;

    return (
        <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
            {/* Backdrop */}
            <View style={styles.backdrop} pointerEvents="none" />

            {/* Sheet */}
            <Animated.View
                style={[
                    styles.sheet,
                    { height: sheetHeight, transform: [{ translateY }] },
                ]}
            >
                {/* Drag handle — own hit-area, initiates pan */}
                <View style={styles.handleHitArea} {...panResponder.panHandlers}>
                    <View style={styles.handleContainer}>
                        <View style={styles.handle} />
                    </View>
                </View>

                <KeyboardAvoidingView
                    behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                    style={styles.keyboardAvoider}
                >
                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.headerTitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Work it out</Text>
                        <TouchableOpacity
                            style={styles.closeButton}
                            onPress={close}
                            accessibilityLabel="Close workpad"
                            accessibilityRole="button"
                            accessibilityHint="Double tap to close the workpad"
                        >
                            <Text style={styles.closeButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>✕</Text>
                        </TouchableOpacity>
                    </View>

                    {/* Body */}
                    <View style={styles.body}>
                        {mode === 'free_draw' && <FreeDrawCanvas />}
                        {mode === 'guided_steps' && effectiveConfig.steps && (
                            <GuidedStepRenderer steps={effectiveConfig.steps} />
                        )}
                    </View>

                    {/* Footer */}
                    <View style={styles.footer}>
                        <TouchableOpacity
                            style={styles.clearButton}
                            onPress={clearStrokes}
                            accessibilityLabel="Clear workpad"
                            accessibilityRole="button"
                            accessibilityHint="Double tap to erase all drawings"
                        >
                            <Text style={styles.clearButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Clear</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={styles.doneButton}
                            onPress={handleDone}
                            accessibilityLabel="Done with workpad"
                            accessibilityRole="button"
                            accessibilityHint="Double tap to finish and close the workpad"
                        >
                            <Text style={styles.doneButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Done ✓</Text>
                        </TouchableOpacity>
                    </View>
                </KeyboardAvoidingView>
            </Animated.View>
        </View>
    );
};

const styles = StyleSheet.create({
    backdrop: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(0,0,0,0.2)',
    },
    sheet: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#FFFFFF',
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
        elevation: 10,
    },
    handleHitArea: {
        paddingTop: 8,
        paddingBottom: 8,
        // 20 pt hit-area expansion on Android
        minHeight: 44,
    },
    handleContainer: {
        alignItems: 'center',
        paddingTop: 4,
        paddingBottom: 4,
    },
    keyboardAvoider: {
        flex: 1,
    },
    handle: {
        width: 40,
        height: 4,
        borderRadius: 2,
        backgroundColor: '#CBD5E1',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        paddingBottom: 12,
    },
    headerTitle: {
        fontSize: 20,
        fontWeight: '700',
        color: '#1E293B',
    },
    closeButton: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: '#F1F5F9',
        alignItems: 'center',
        justifyContent: 'center',
    },
    closeButtonText: {
        fontSize: 18,
        fontWeight: '700',
        color: '#64748B',
    },
    body: {
        flex: 1,
        paddingHorizontal: 16,
    },
    footer: {
        flexDirection: 'row',
        gap: 12,
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderTopWidth: 1,
        borderTopColor: '#E2E8F0',
    },
    clearButton: {
        flex: 1,
        height: 48,
        borderRadius: 12,
        backgroundColor: '#F1F5F9',
        alignItems: 'center',
        justifyContent: 'center',
    },
    clearButtonText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#64748B',
    },
    doneButton: {
        flex: 2,
        height: 48,
        borderRadius: 12,
        backgroundColor: '#22c55e',
        alignItems: 'center',
        justifyContent: 'center',
    },
    doneButtonText: {
        fontSize: 16,
        fontWeight: '700',
        color: '#FFFFFF',
    },
});
